import type { ReactNode } from "react";

import { useAuth } from "./auth-context";

/**
 * `docs/spec/permissions.md` §6: an ability is truthy or falsy, never
 * `=== true` — a grant made by explicit membership arrives as an array of
 * membership ids. Every ability read in the client goes through here so that
 * rule is applied in exactly one place.
 */
export function granted(abilities: Record<string, unknown> | undefined, ability: string): boolean {
  return Boolean(abilities?.[ability]);
}

export function useAbility(id: string | undefined, ability: string): boolean {
  const { abilitiesFor } = useAuth();
  return granted(abilitiesFor(id), ability);
}

export interface AbilityGateProps {
  /** Record id whose policy carries the ability; usually the workspace id. */
  id?: string | undefined;
  ability?: string;
  /** An already-computed condition, ANDed with the ability when both are given. */
  allow?: boolean;
  fallback?: ReactNode;
  children: ReactNode;
}

/** Renders `children` only when the ability holds, else `fallback` (nothing by default). */
export function AbilityGate({
  id,
  ability,
  allow,
  fallback = null,
  children,
}: AbilityGateProps): ReactNode {
  const granted = useAbility(id, ability ?? "");
  const allowed = (ability === undefined || granted) && (allow ?? true);
  return allowed ? children : fallback;
}
